import * as React from "react"
import { useTranslation } from "react-i18next"
import * as SelectPrimitive from "@radix-ui/react-select"
import { clsx } from "clsx"
import { Icon } from "@/components/icons/Icon"
import { analytics } from "@/telemetry/amplitude"
import { Spinner } from "./Spinner"
import { Button } from "./Button"

const Select: React.FC<
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> & {
		trackingLabel: string
		trackingContext?: string
	}
> = ({ trackingLabel, trackingContext, ...props }) => {
	//Since there is no target to be passed to trackEvent, we pass trackingContext as breadcrumbs
	return (
		<SelectPrimitive.Root
			{...props}
			onOpenChange={(open) => {
				props.onOpenChange?.(open)
				if (open) {
					analytics.trackEvent({
						type: "click",
						label: `open_${trackingLabel}`,
						breadcrumbs: trackingContext,
					})
				}
			}}
			onValueChange={(value) => {
				props.onValueChange?.(value)
				analytics.trackEvent({
					type: "click",
					label: `select_${trackingLabel}`,
					value,
					breadcrumbs: trackingContext,
				})
			}}
		/>
	)
}

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
		isLoading?: boolean
		tooltip?: string
		tooltipDelay?: number
		hidesArrow?: boolean
		buttonClassName?: string
	}
>(
	(
		{
			className,
			children,
			isLoading,
			tooltip,
			tooltipDelay,
			hidesArrow,
			buttonClassName,
			...props
		},
		ref,
	) => {
		const { t } = useTranslation("components")
		return (
			<SelectPrimitive.Trigger
				ref={ref}
				className={clsx(
					"electron-no-drag ring-offset-background placeholder:text-muted-foreground flex h-8 w-full flex-row items-center justify-start rounded-md border-0 bg-transparent py-2 text-base-300 hover:text-base-100 disabled:opacity-50",
					className,
				)}
				disabled={isLoading}
				{...props}
			>
				<Button
					trackingLabel="open_select"
					aria-label={t("select.ariaOpenSelectionMode")}
					tooltip={tooltip}
					tooltipDelay={tooltipDelay}
					size="custom"
					variant="custom"
					className={clsx("items-center", buttonClassName)}
				>
					{children}
					{isLoading && <Spinner size={8} className="ml-1" />}
					{!isLoading && !hidesArrow && (
						<SelectPrimitive.Icon asChild>
							<Icon name="down" className="size-4 opacity-50" />
						</SelectPrimitive.Icon>
					)}
				</Button>
			</SelectPrimitive.Trigger>
		)
	},
)
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
	<SelectPrimitive.Portal>
		<SelectPrimitive.Content
			ref={ref}
			className={clsx(
				"bg-popover text-popover-foreground relative z-50 min-w-32 overflow-hidden rounded-md border border-base-700 bg-base-800 shadow-md animate-in fade-in-0",
				position === "popper" &&
					(props.side === "top" ? "-translate-y-1" : "translate-y-1"),
				className,
			)}
			position={position}
			{...props}
		>
			<SelectPrimitive.Viewport
				className={clsx(
					"p-1",
					position === "popper" &&
						"h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
				)}
			>
				{children}
			</SelectPrimitive.Viewport>
		</SelectPrimitive.Content>
	</SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Label>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Label
		ref={ref}
		className={clsx("py-1.5 pl-8 pr-2 text-xs font-semibold", className)}
		{...props}
	/>
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
	<SelectPrimitive.Item
		ref={ref}
		className={clsx(
			"focus:text-accent-600-foreground relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-xs outline-none focus:bg-accent-600 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
			className,
		)}
		{...props}
	>
		<span className="absolute left-2 flex size-3.5 items-center justify-center">
			<SelectPrimitive.ItemIndicator>
				<Icon name="check" className="size-4" />
			</SelectPrimitive.ItemIndicator>
		</span>

		<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
	</SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Separator>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Separator
		ref={ref}
		className={clsx("-mx-1 my-1 h-px bg-base-700", className)}
		{...props}
	/>
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
	Select,
	SelectGroup,
	SelectValue,
	SelectTrigger,
	SelectContent,
	SelectLabel,
	SelectItem,
	SelectSeparator,
}
